import { xNoiseSanPlot } from '../x/xNoiseSanPlot';
import { reimAbsolute } from './reimAbsolute';
import { reimPhaseCorrection } from './reimPhaseCorrection';
/**
 * Implementation of the algorithm for automatic phase correction: A robust, general automatic phase
 * correction algorithm for high-resolution NMR data. 10.1002/mrc.4586
 *
 * @param data - complex spectrum
 * @param options - options
 */
export function reimAutoPhaseCorrection(data, options = {}) {
    const { re, im } = data;
    const length = re.length;
    const { magnitudeMode = true, minRegSize = 30, factorNoise = 3, maxDistanceToJoin = 256, reverse = false, } = options;
    const magnitudeData = magnitudeMode ? reimAbsolute(data) : re;
    const ds = holoborodko(magnitudeData);
    const peaksDs = robustBaseLineRegionsDetection(ds, {
        maxDistanceToJoin,
        magnitudeMode,
        factorNoise,
    });
    const peaksSp = robustBaseLineRegionsDetection(magnitudeData, {
        maxDistanceToJoin,
        magnitudeMode,
        factorNoise,
    });
    const finalPeaks = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        finalPeaks[i] = peaksSp[i] && peaksDs[i];
    }
    // Once the regions are detected, we auto phase each of them separately.
    // This part can be put inside a function
    const indexMask = reverse ? (i) => length - i : (i) => i;
    let i = -1;
    let x0 = 0;
    const res = [];
    while (i < length) {
        //phase first region
        const reTmp = [];
        const imTmp = [];
        //Look for the first 1 in the array
        while (!finalPeaks[++i] && i < length) {
            //Add some extra points(0.1 ppm) at rigth and left sides of the region.
            x0 = indexMask(i);
        }
        for (; finalPeaks[i] && i < length; i++) {
            reTmp.push(re[i]);
            imTmp.push(im[i]);
            i++;
        }
        if (reTmp.length > minRegSize) {
            res.push(autoPhaseRegion(reTmp, imTmp, x0));
        }
    }
    // Still some corrections needed. In the paper they remove the outlayers interatively
    // until they can perform a regression witout bad points. Can someone help here?
    const [ph1, ph0] = weightedLinearRegression(res.map((r) => r.x0 / length), res.map((r) => r.ph0), res.map((r) => r.area / 1e11));
    const phased = reimPhaseCorrection({ re, im }, (ph0 * Math.PI) / 180, (ph1 * Math.PI) / 180, { reverse });
    return { data: phased, ph0, ph1 };
}
/**
 * AutoPhaseRegion.
 *
 * @param re - Array of Number.
 * @param im - Array of Number.
 * @param x0 - Number.
 * @returns Region.
 */
function autoPhaseRegion(re, im, x0) {
    let start = -180;
    let stop = 180;
    const nSteps = 6;
    let maxSteps = 5;
    let bestAng = 0;
    let minArea = Number.MAX_SAFE_INTEGER;
    while (maxSteps > 0) {
        const dAng = (stop - start) / (nSteps + 1);
        for (let i = start; i <= stop; i += dAng) {
            const phased = reimPhaseCorrection({ re, im }, toRadians(i), 0);
            const negArea = getNegArea(phased.re);
            if (negArea < minArea) {
                [minArea, bestAng] = [negArea, i];
            }
        }
        start = bestAng - dAng;
        stop = bestAng + dAng;
        maxSteps--;
    }
    // Calculate the area for the best angle
    const phased = reimPhaseCorrection({ re, im }, toRadians(bestAng), 0);
    let area = 0;
    let sumX = 0;
    for (let j = 0; j < re.length; j++) {
        area += phased.re[j];
        sumX += phased.re[j] * (j + x0);
    }
    return { ph0: bestAng, area, x0: sumX / area };
}
/**
 * Holoborodko.
 *
 * @param s - Array of float.
 * @returns Array of float.
 */
function holoborodko(s) {
    const dk = new Float64Array(s.length);
    for (let i = 5; i < s.length - 5; i++) {
        dk[i] =
            (42 * (s[i + 1] - s[i - 1]) +
                48 * (s[i + 2] - s[i - 2]) +
                27 * (s[i + 3] + s[i - 3]) +
                8 * (s[i + 4] - s[i - 4]) +
                s[i + 5] -
                s[i - 5]) /
                512;
    }
    //Fill the borders
    for (let i = 0; i < 5; i++) {
        dk[i] = dk[5];
        dk[s.length - i - 1] = dk[s.length - 6];
    }
    return dk;
}
/**
 * RobustBaseLineRegionsDetection.
 *
 * @param s
 * @param options
 * @param options.magnitudeMode
 * @param options.maxDistanceToJoin
 * @param options.factorNoise
 */
function robustBaseLineRegionsDetection(s, options) {
    const { maxDistanceToJoin, magnitudeMode, factorNoise } = options;
    const mask = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) {
        mask[i] = 0;
    }
    let change = true;
    while (change) {
        const noiseLevel = xNoiseSanPlot(s, { magnitudeMode });
        const cutOff = factorNoise * noiseLevel.positive;
        change = false;
        for (let i = 0; i < s.length; i++) {
            if (Math.abs(s[i]) > cutOff && !mask[i]) {
                change = true;
                mask[i] = 1;
            }
        }
    }
    // Clean up mask by merging peaks blocks, separated by just a few points(4??).
    let count = 0;
    let prev = 0;
    for (let i = 0; i < s.length; i++) {
        if (!mask[i]) {
            count++;
        }
        else {
            if (count < maxDistanceToJoin) {
                for (let j = 0; j <= count; j++) {
                    mask[prev + j] = 1;
                }
            }
            while (mask[++i] && i < s.length)
                ;
            prev = i;
            count = 0;
        }
    }
    return mask;
}
/**
 * WeightedLinearRegression.
 *
 * @param x
 * @param y
 * @param w
 */
function weightedLinearRegression(x, y, w) {
    let sxtw = 0;
    let swx = 0;
    let sw = 0;
    let sxtwy = 0;
    let swy = 0;
    for (let i = 0; i < x.length; i++) {
        sxtw += x[i] * x[i] * w[i];
        swx += x[i] * w[i];
        sw += w[i];
        sxtwy += x[i] * w[i] * y[i];
        swy += w[i] * y[i];
    }
    /* Just to know what is the matrix system that we solve
     let Mx=[[sxtw, swx], [swx, sw]];
     let My=[[sxtwy], [swy]];
    */
    //Mx inverse
    const detMx = sxtw * sw - swx * swx;
    const inMx = [
        [sw / detMx, -swx / detMx],
        [-swx / detMx, sxtw / detMx],
    ];
    return [
        inMx[0][0] * sxtwy + inMx[0][1] * swy,
        inMx[1][0] * sxtwy + inMx[1][1] * swy,
    ];
}
const toRadians = (degree) => (degree * Math.PI) / 180;
const getNegArea = (data) => {
    let area = 0;
    for (const element of data) {
        if (element < 0)
            area -= element;
    }
    return area;
};
//# sourceMappingURL=reimAutoPhaseCorrection.js.map